import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { StockFormComponent } from '../stock-form/stock-form.component';
import { ProductStockService } from 'app/core/services/admin/product-stock.service';
import { FarmsService } from 'app/core/services/admin/farms.service';
import { ProductsService } from 'app/core/services/admin/products.service';
import { Farm } from 'app/core/models/admin/farms.model';
import { Product } from 'app/core/models/admin/products.model';
import { Subscription, take } from 'rxjs';
import {
  FilterDialogComponent,
  FilterData,
} from '../filter-dialog/filter-dialog.component';
import { Router } from '@angular/router';
import { result } from 'lodash';
import { ScreenUtils } from 'app/shared/utils/screen-utils';
import { TerritoryService } from 'app/core/services/squads/territory.service';

@Component({
  selector: 'app-list-stock',
  templateUrl: './list-stock.component.html',
  styleUrls: ['./list-stock.component.scss'],
})
export class ListStockComponent implements OnInit, OnDestroy {
  stock: any[] = [];
  isLoading: boolean = false;
  error: string = '';
  pagination = {
    length: 0,
    page: 0,
    size: 100,
  };

  // Filtros
  filterFarm: number = null;
  filterProduct: string = '';
  filterBatch: string = '';
  filterValidity: Date = null;
  filterMode: string = '';
  filterCreatedAt: Date = null;
  filterUpdatedAt: Date = null;
  filterStockDate: Date = null;
  farms: Farm[] = [];
  products: Product[] = [];
  isDownloading: boolean = false;

  private subscription: Subscription;

  sort = {
    column: '',
    direction: '' as 'asc' | 'desc' | '',
  };

  searchTerm: string = '';

  isMobile: boolean;

  filterRegional: number = null;
  regionals: { id: number; name: string }[] = [];

  private farmsLoaded = false;
  private regionalsLoaded = false;

  constructor(
    private router: Router,
    private productStockService: ProductStockService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private farmsService: FarmsService,
    private productsService: ProductsService,
    private territoryService: TerritoryService
  ) {}

  ngOnInit(): void {
    this.isMobile = ScreenUtils.isTelaPequena();
    this.loadRegionals();
    this.loadFarms();
    this.loadProducts();
  }

  loadFarms(): void {
    this.farmsService.getFarms().subscribe({
      next: data => {
        this.farms = data;
        this.farmsLoaded = true;
        this.checkIfCanLoadStock();
        this.cdr.markForCheck();
      },
      error: err => {
        console.error('Erro ao carregar fazendas:', err);
      },
    });
  }

  loadRegionals(): void {
    this.territoryService
      .getRegionals()
      .pipe(take(1))
      .subscribe({
        next: (regionals: any[]) => {
          this.regionals = regionals.map(r => ({ id: r.id, name: r.name }));
          this.regionalsLoaded = true;
          this.checkIfCanLoadStock();
          this.cdr.markForCheck();
        },
        error: err => {
          console.error('Erro ao carregar regionais:', err);
        },
      });
  }

  // ✅ Novo método
  checkIfCanLoadStock(): void {
    if (this.farmsLoaded && this.regionalsLoaded) {
      this.loadStock();
    }
  }

  loadProducts(): void {
    this.productsService.getProducts().subscribe({
      next: data => {
        this.products = data;
        this.cdr.markForCheck();
      },
      error: err => {
        console.error('Erro ao carregar produtos:', err);
      },
    });
  }

  loadStock(): void {
    this.isLoading = true;
    if (this.subscription) this.subscription.unsubscribe();
    this.subscription = this.productStockService.getStock().subscribe({
      next: data => {
        this.stock = data.stock;

        this.pagination.length = data.totalItems || this.stock.length;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: err => {
        console.error('❌ Erro ao carregar o estoque:', err);
        this.error = 'Erro ao carregar o estoque!';
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Converte uma string de data para um objeto Date.
   * Se a string contiver '/' assume o formato "dd/mm/yyyy hh:mm:ss".
   * Se estiver no formato "YYYY-MM-DD", cria uma data em horário local.
   */
  private parseDate(dateInput: any): Date {
    if (!dateInput) {
      return null;
    }
    // Se já for um objeto Date, retorne-o
    if (dateInput instanceof Date) {
      return dateInput;
    }
    // Se não for uma string, tenta convertê-lo diretamente
    if (typeof dateInput !== 'string') {
      return new Date(dateInput);
    }
    // Se a string contém '/', assume o formato "dd/mm/yyyy hh:mm:ss"
    if (dateInput.includes('/')) {
      const [datePart, timePart] = dateInput.split(' ');
      const [day, month, year] = datePart.split('/').map(Number);
      const [hours = 0, minutes = 0, seconds = 0] = timePart
        ? timePart.split(':').map(Number)
        : [];
      return new Date(year, month - 1, day, hours, minutes, seconds);
    }
    // Se estiver no formato "YYYY-MM-DD", cria a data em horário local
    const isoMatch = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
    // Fallback para outros formatos
    return new Date(dateInput);
  }

  get filteredProducts(): Product[] {
    return this.products;
  }

  // Função auxiliar robusta para comparar duas datas ignorando as horas
  private isSameDate(date1: any, date2: any): boolean {
    let d1 = date1;
    let d2 = date2;
    if (d1 && typeof d1.toDate === 'function') {
      d1 = d1.toDate();
    } else if (!(d1 instanceof Date)) {
      d1 = new Date(d1);
    }
    if (d2 && typeof d2.toDate === 'function') {
      d2 = d2.toDate();
    } else if (!(d2 instanceof Date)) {
      d2 = new Date(d2);
    }
    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
      return false;
    }
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }

  /*get filteredStock(): any[] {
      return this.stock.filter(item => {
        const matchFarm = !this.filterFarm || item.farm_id === this.filterFarm;
        const matchProduct =
          !this.filterProduct ||
          (item.product &&
            item.product.description
              .toLowerCase()
              .includes(this.filterProduct.toLowerCase()));
        const batchMatches =
          item.batch &&
          item.batch.some(batch => {
            const matchBatch =
              !this.filterBatch ||
              (batch.batch &&
                batch.batch
                  .toLowerCase()
                  .includes(this.filterBatch.toLowerCase()));
            const matchValidity =
              !this.filterValidity ||
              (batch.validity &&
                this.isSameDate(
                  this.parseDate(batch.validity),
                  this.filterValidity
                ));
            const matchMode =
              !this.filterMode ||
              (batch.mode &&
                batch.mode.toLowerCase() === this.filterMode.toLowerCase());
            const matchCreatedAt =
              !this.filterCreatedAt ||
              (batch.created_at &&
                this.isSameDate(
                  new Date(batch.created_at),
                  this.filterCreatedAt
                ));
            const matchUpdatedAt =
              !this.filterUpdatedAt ||
              (batch.updated_at &&
                this.isSameDate(
                  new Date(batch.updated_at),
                  this.filterUpdatedAt
                ));
            const matchStockDate =
              !this.filterStockDate ||
              (batch.stock_date &&
                this.isSameDate(
                  this.parseDate(batch.stock_date),
                  this.filterStockDate
                ));
            return (
              matchBatch &&
              matchValidity &&
              matchMode &&
              matchCreatedAt &&
              matchUpdatedAt &&
              matchStockDate
            );
          });
        return matchFarm && matchProduct && batchMatches;
      });
    }*/

  get filteredStock(): any[] {
    let result = [...this.stock];

    // Etapa 1: filtro de busca global
    if (this.searchTerm?.trim()) {
      const term = this.searchTerm.toLowerCase();

      result = result.filter(item => {
        const matchId = item.id?.toString().includes(term);
        const matchFarm = item.farm?.toLowerCase().includes(term);
        const matchProduct = item.product?.description
          ?.toLowerCase()
          .includes(term);

        const batchMatches = item.batch?.some(batch => {
          const matchBatch = batch.batch?.toLowerCase().includes(term);
          const matchMode = batch.mode?.toLowerCase().includes(term);
          const matchQuantity = batch.quantity?.toString().includes(term);

          let matchValidity = false;
          if (batch.validity) {
            try {
              const date = new Date(batch.validity);
              const formatted =
                `${date.getDate().toString().padStart(2, '0')}/` +
                `${(date.getMonth() + 1).toString().padStart(2, '0')}/` +
                `${date.getFullYear()}`;
              matchValidity = formatted.includes(term);
            } catch (e) {
              console.error('Erro ao formatar validade:', e);
            }
          }

          return matchBatch || matchMode || matchQuantity || matchValidity;
        });

        return matchId || matchFarm || matchProduct || batchMatches;
      });
    }

    // Etapa 2: aplicação dos filtros refinados
    result = result.filter(item => {
      const matchFarm = !this.filterFarm || item.farm_id === this.filterFarm;
      const matchProduct =
        !this.filterProduct ||
        (item.product &&
          item.product.description
            .toLowerCase()
            .includes(this.filterProduct.toLowerCase()));

      // ✅ Novo filtro por regional
      const matchRegional =
        !this.filterRegional || item.regional?.id === this.filterRegional;

      const batchMatches =
        item.batch &&
        item.batch.some(batch => {
          const matchBatch =
            !this.filterBatch ||
            (batch.batch &&
              batch.batch
                .toLowerCase()
                .includes(this.filterBatch.toLowerCase()));
          const matchValidity =
            !this.filterValidity ||
            (batch.validity &&
              this.isSameDate(
                this.parseDate(batch.validity),
                this.filterValidity
              ));
          const matchMode =
            !this.filterMode ||
            (batch.mode &&
              batch.mode.toString().trim().toLowerCase() ===
                this.filterMode.trim().toLowerCase());
          const matchCreatedAt =
            !this.filterCreatedAt ||
            (batch.created_at &&
              this.isSameDate(
                new Date(batch.created_at),
                this.filterCreatedAt
              ));
          const matchUpdatedAt =
            !this.filterUpdatedAt ||
            (batch.updated_at &&
              this.isSameDate(
                new Date(batch.updated_at),
                this.filterUpdatedAt
              ));
          const matchStockDate =
            !this.filterStockDate ||
            (batch.stock_date &&
              this.isSameDate(
                this.parseDate(batch.stock_date),
                this.filterStockDate
              ));

          return (
            (!this.filterBatch || matchBatch) &&
            (!this.filterValidity || matchValidity) &&
            (!this.filterMode || matchMode) &&
            (!this.filterCreatedAt || matchCreatedAt) &&
            (!this.filterUpdatedAt || matchUpdatedAt) &&
            (!this.filterStockDate || matchStockDate)
          );
        });

      return matchFarm && matchProduct && matchRegional && batchMatches; // ⬅️ Incluído `matchRegional`
    });

    // Etapa 3: ordenação
    if (this.sort.column) {
      result = result.sort((a, b) => {
        const valueA = this.getSortableValue(a, this.sort.column);
        const valueB = this.getSortableValue(b, this.sort.column);

        if (valueA < valueB) return this.sort.direction === 'asc' ? -1 : 1;
        if (valueA > valueB) return this.sort.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }

  openStockForm(item?: any, batchIndex?: number): void {
    const dialogRef = this.dialog.open(StockFormComponent, {
      width: '600px',
      data: { stockData: item, batchIndex: batchIndex || null },
    });
    dialogRef.afterClosed().subscribe(result => {
      //console.log("result", result);
      if (result) {
        //console.log(result.batchDataBaseDelete);
        //console.log(result.data);

        if (result.batchDataBaseDelete) {
          this.loadStock();
          this.scrollToTop();
        }

        if (result.data) {
          this.loadStock();
          this.scrollToTop();
        }
      }
    });
  }

  createNewStock(): void {
    this.openStockForm();
  }

  // Abre o diálogo de filtros
  openFilterDialog(): void {
    const dialogRef = this.dialog.open(FilterDialogComponent, {
      width: '700px',
      data: <FilterData>{
        filterFarm: this.filterFarm,
        filterProduct: this.filterProduct,
        filterBatch: this.filterBatch,
        filterValidity: this.filterValidity,
        filterMode: this.filterMode,
        filterCreatedAt: this.filterCreatedAt,
        filterUpdatedAt: this.filterUpdatedAt,
        filterStockDate: this.filterStockDate,
        filterRegional: this.filterRegional, // ✅ ESSENCIAL!
        farms: this.farms,
        products: this.products,
        regionals: this.regionals, // ⬅️ Adicionado aqui
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log('Filtros aplicados:', result);
        this.filterFarm = result.filterFarm;
        this.filterProduct = result.filterProduct;
        this.filterBatch = result.filterBatch;
        this.filterValidity = result.filterValidity;
        this.filterMode = result.filterMode;
        this.filterCreatedAt = result.filterCreatedAt;
        this.filterUpdatedAt = result.filterUpdatedAt;
        this.filterStockDate = result.filterStockDate;
        this.filterRegional = result.filterRegional; // ✅ Aqui!
        this.cdr.markForCheck();
      }
    });
  }

  searchStock(): void {
    // Se não houver termo, recarrega o padrão (100)
    if (!this.searchTerm || !this.searchTerm.trim()) {
      this.loadStock();
      return;
    }

    // Ao pesquisar explicitamente, traz todos os itens do servidor (sem limite)
    this.isLoading = true;
    if (this.subscription) this.subscription.unsubscribe();
    this.subscription = this.productStockService.getStock().subscribe({
      next: data => {
        this.stock = data.stock;
        this.pagination.length = data.totalItems || this.stock.length;
        // Quando o usuário pesquisa, mostramos todos (sem page size limit)
        this.pagination.page = 0;
        this.cdr.markForCheck();
        this.isLoading = false;
      },
      error: err => {
        console.error('Erro ao pesquisar estoque:', err);
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  trackByFn(index: number, item: any): any {
    return item.id;
  }

  getBatchDate(batch: any): string {
    const created = new Date(batch.created_at);
    const updated = new Date(batch.updated_at);
    if (created.getTime() === updated.getTime()) {
      return `Criado: ${updated.toLocaleString()}`;
    } else if (updated.getTime() > created.getTime()) {
      return `Atualizado: ${updated.toLocaleString()}`;
    }
    return updated.toLocaleString();
  }

  // Exibe alerta para o nível mínimo se o total estiver a até 5 unidades do mínimo
  shouldShowMinAlert(item: any): boolean {
    if (item.total_stock == null || item.minimun_level == null) return false;
    return item.total_stock <= item.minimun_level + 5;
  }

  // Exibe alerta para validade se a data estiver a 15 dias ou menos da data atual
  shouldShowValidityAlert(validity: string): boolean {
    if (!validity) return false;
    const validityDate = this.parseDate(validity);
    const today = new Date();
    const diffDays =
      (validityDate.getTime() - today.getTime()) / (1000 * 3600 * 24);
    return diffDays <= 15;
  }

  // Retorna o nível de alerta para o lote: 'none', 'medium' ou 'high'
  getBatchAlertLevel(batch: any): string {
    if (!batch || (!batch.created_at && !batch.updated_at)) return 'none';
    const lastChange = batch.updated_at
      ? new Date(batch.updated_at)
      : new Date(batch.created_at);
    const today = new Date();
    const diffDays =
      (today.getTime() - lastChange.getTime()) / (1000 * 3600 * 24);
    //console.log('diffDays:', diffDays);
    if (diffDays >= 30) return 'high';
    if (diffDays >= 15) return 'medium';
    return 'none';
  }

  getProductColor(productId: number): string {
    const product = this.products.find(prod => prod.id === productId);
    return product ? product.color : '#fff';
  }

  downloadStock(): void {
    this.isDownloading = true;
    this.productStockService.exportStock().subscribe({
      next: blob => {
        const now = new Date();
        const formattedDate = now
          .toLocaleDateString('pt-BR')
          .replace(/\//g, '-');
        const formattedTime = now
          .toLocaleTimeString('pt-BR')
          .replace(/:/g, '-');
        const fileName = `estoque_${formattedDate}_${formattedTime}.xlsx`;

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.isDownloading = false;
      },
      error: err => {
        console.error('Erro no download do estoque', err);
        this.isDownloading = false;
      },
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /**
   * Ir para a lista de estoques controlados
   */
  switchTab(): void {
    this.router.navigate(['/stock']);
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  sortBy(column: string) {
    if (this.sort.column === column) {
      this.sort.direction = this.sort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.sort.column = column;
      this.sort.direction = 'asc';
    }

    this.filteredStock.sort((a, b) => {
      const valueA = this.getSortableValue(a, column);
      const valueB = this.getSortableValue(b, column);

      if (valueA < valueB) return this.sort.direction === 'asc' ? -1 : 1;
      if (valueA > valueB) return this.sort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  getSortableValue(item: any, column: string): any {
    switch (column) {
      case 'id':
        return item.id;
      case 'farm':
        return item.farm?.toLowerCase();
      case 'product':
        return item.product?.description?.toLowerCase();
      case 'min':
        return item.minimun_level;
      case 'total':
        return item.total_stock;
      case 'batch':
        return item.batch[0]?.batch?.toLowerCase();
      case 'validity':
        return new Date(item.batch[0]?.validity || '');
      case 'mode':
        return item.batch[0]?.mode?.toLowerCase();
      case 'quantity':
        return item.batch[0]?.quantity;
      default:
        return '';
    }
  }

  getModeColor(mode: string): string {
    if (!mode) return '#9ca3af'; // gray para indefinido
    const normalized = mode.toLowerCase();
    switch (normalized) {
      case 'ondemand':
        return '#f97316'; // laranja (OnDemand)
      case 'onfarm':
        return '#10b981'; // verde (OnFarm)
      default:
        return '#6b7280'; // cinza padrão
    }
  }

  getModeLabel(mode: string): string {
    if (!mode) return '-';
    const normalized = mode.toLowerCase();
    switch (normalized) {
      case 'ondemand':
        return 'OnDemand';
      case 'onfarm':
        return 'OnFarm';
      default:
        return mode.substring(0, 2).toUpperCase(); // fallback genérico
    }
  }
}
